/**
 * Quick Switcher (Cmd+P) — lightweight page/tab switcher.
 *
 * Only searches: open tabs, recent pages, module pages.
 * No server calls. Instant fuzzy filter.
 */

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fuzzyFilter } from "@/lib/fuzzy";
import { registerShortcut } from "@/lib/keyboard";
import { readRecentPages, rememberRecentPage } from "@/lib/recent-pages";
import { useTabStore } from "@/lib/tabs";
import { getModuleDefs } from "@/lib/workspaces";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRightLeft, FileText, History } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface QuickItem {
	id: string;
	label: string;
	path: string;
	group: "tabs" | "recent" | "pages";
}

export function QuickSwitcher() {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const navigate = useNavigate();
	const { t } = useTranslation();
	const tabs = useTabStore((s) => s.tabs);

	// Register Cmd+P
	useEffect(() => {
		return registerShortcut({
			id: "navigation.quick-switcher",
			keys: "meta+p",
			label: "shortcuts_quick_switcher",
			displayKeys: "⌘P",
			category: "navigation",
			action: () => setOpen(true),
		});
	}, []);

	// Build all searchable items
	const allItems = useMemo<QuickItem[]>(() => {
		const items: QuickItem[] = [];

		// Open tabs
		for (const tab of tabs) {
			items.push({
				id: `tab:${tab.id}`,
				label: tab.title,
				path: tab.path,
				group: "tabs",
			});
		}

		// Recent pages
		for (const page of readRecentPages(8)) {
			if (!items.some((i) => i.path === page.to)) {
				items.push({
					id: `recent:${page.to}`,
					label: page.label,
					path: page.to,
					group: "recent",
				});
			}
		}

		// Module pages
		for (const mod of getModuleDefs()) {
			for (const item of mod.items) {
				if (!items.some((i) => i.path === item.to)) {
					items.push({
						id: `page:${item.to}`,
						label: t(item.labelKey, { defaultValue: item.labelKey }),
						path: item.to,
						group: "pages",
					});
				}
			}
		}

		return items;
	}, [tabs, t]);

	// Filter items
	const filtered = useMemo(() => {
		if (!query.trim()) return allItems;
		return fuzzyFilter(allItems, query, (i) => `${i.label} ${i.path}`);
	}, [allItems, query]);

	const go = useCallback(
		(path: string, label: string) => {
			setOpen(false);
			setQuery("");
			rememberRecentPage(path, label);
			navigate({ to: path });
		},
		[navigate],
	);

	useEffect(() => {
		if (!open) setQuery("");
	}, [open]);

	const tabItems = filtered.filter((i) => i.group === "tabs");
	const recentItems = filtered.filter((i) => i.group === "recent");
	const pageItems = filtered.filter((i) => i.group === "pages");

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent className="p-0 max-w-md overflow-hidden [&>button:last-child]:hidden">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={t("quick_switcher_placeholder")}
						value={query}
						onValueChange={setQuery}
					/>
					<CommandList className="max-h-80">
						<CommandEmpty>{t("no_results")}</CommandEmpty>

						{tabItems.length > 0 && (
							<CommandGroup heading={t("open_tabs")}>
								{tabItems.map((item) => (
									<CommandItem
										key={item.id}
										value={item.id}
										onSelect={() => go(item.path, item.label)}
										className="gap-2"
									>
										<ArrowRightLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
										<span className="truncate">{item.label}</span>
										<span className="ml-auto text-[10px] text-muted-foreground font-mono truncate max-w-[120px]">
											{item.path}
										</span>
									</CommandItem>
								))}
							</CommandGroup>
						)}

						{recentItems.length > 0 && (
							<CommandGroup heading={t("recent")}>
								{recentItems.map((item) => (
									<CommandItem
										key={item.id}
										value={item.id}
										onSelect={() => go(item.path, item.label)}
										className="gap-2"
									>
										<History className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
										<span className="truncate">{item.label}</span>
									</CommandItem>
								))}
							</CommandGroup>
						)}

						{pageItems.length > 0 && (
							<CommandGroup heading={t("pages")}>
								{pageItems.slice(0, 10).map((item) => (
									<CommandItem
										key={item.id}
										value={item.id}
										onSelect={() => go(item.path, item.label)}
										className="gap-2"
									>
										<FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
										<span className="truncate">{item.label}</span>
									</CommandItem>
								))}
							</CommandGroup>
						)}
					</CommandList>
				</Command>
			</DialogContent>
		</Dialog>
	);
}
